import cx from "classnames";
import {
  type ComponentProps,
  Fragment,
  type ReactElement,
  isValidElement,
} from "react";

import { Breadcrumb } from "metabase/common/components/Breadcrumb";
import { Box, Flex } from "metabase/ui";
import type { ColorName } from "metabase/ui/colors/types";

import type { DataSourcePart } from "../QuestionDataSource/utils";

import HeaderBreadcrumbsS from "./HeaderBreadcrumbs.module.css";

const HeaderBreadcrumb = (props: ComponentProps<typeof Breadcrumb>) => (
  <Breadcrumb className={HeaderBreadcrumbsS.HeaderBreadcrumb} {...props} />
);

function getBadgeInactiveColor({
  variant,
  isLast,
}: {
  variant: "head" | "subhead";
  isLast: boolean;
}) {
  // "text-disabled" was used here to de-emphasize inactive crumbs, but these
  // are enabled, clickable links, not disabled controls — that token's
  // contrast (~2.85:1 on white) fails WCAG 1.4.3 (text_contrast_sufficient).
  return isLast && variant === "head" ? "text-primary" : "text-secondary";
}

interface HeadBreadcrumbsProps {
  className?: string;
  variant?: "head" | "subhead";
  parts: DataSourcePart[];
  divider?: string | ReactElement;
  inactiveColor?: ColorName;
  isObjectDetail?: boolean;
}

export function HeadBreadcrumbs({
  variant = "head",
  parts,
  divider,
  inactiveColor,
  ...props
}: HeadBreadcrumbsProps) {
  const { isObjectDetail, ...rest } = props;
  return (
    <Flex
      align="center"
      wrap="wrap"
      data-testid="head-crumbs-container"
      className={cx(HeaderBreadcrumbsS.Container, {
        [HeaderBreadcrumbsS.headVariant]: variant === "head",
      })}
      {...rest}
    >
      {parts.map((part, index) => {
        const isLast = index === parts.length - 1;
        const badgeInactiveColor =
          inactiveColor || getBadgeInactiveColor({ variant, isLast });
        return (
          <Fragment key={index}>
            {isDataSourceReactElement(part) ? (
              part
            ) : (
              <HeaderBreadcrumb
                to={part.href}
                color={badgeInactiveColor}
                icon={part.icon}
              >
                {part.name}
              </HeaderBreadcrumb>
            )}
            {!isLast &&
              (isDividerReactElement(divider) ? (
                divider
              ) : (
                <Divider char={divider} />
              ))}
          </Fragment>
        );
      })}
    </Flex>
  );
}

function Divider({ char = "/" }: { char?: string }) {
  return (
    <Box component="span" className={HeaderBreadcrumbsS.HeaderBreadcrumbs}>
      {char}
    </Box>
  );
}

function isDataSourceReactElement(part: DataSourcePart): part is ReactElement {
  return isValidElement(part);
}

function isDividerReactElement(
  divider?: string | ReactElement,
): divider is ReactElement {
  return isValidElement(divider);
}

HeadBreadcrumbs.Breadcrumb = HeaderBreadcrumb;
